import CustomModal from '@components/CustomModal';
import TutorialSpotlight from '@components/TutorialSpotlight';
import WolfButton from '@components/ui/WolfButton';
import { TUTORIAL_STEPS, TUTORIAL_VERSION } from '@config/tutorialSteps';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { UserPreferences, updateUserPreferencesRequest } from '@models/user.model';
import { Box, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setUserPreferences } from '@redux/reducers/user.reducer';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TutorialModal: React.FC<TutorialModalProps> = ({ isOpen, onClose }) => {
  const localeTutorial = useTranslation('tutorial');
  const localeActionMessages = useTranslation('actionMessages');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();

  const preferences = useAppSelector((state) => state.user.preferences);
  const tutorialStartStep = useAppSelector((state) => state.user.tutorialStartStep);

  const [currentStep, setCurrentStep] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);

  const currentStepData = TUTORIAL_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;

  // Reset to starting step when modal opens and scroll sidebar to top
  useEffect(() => {
    if (isOpen) {
      const startStep = tutorialStartStep ?? 0;
      setCurrentStep(startStep);

      // Scroll sidebar to top when tutorial opens
      const sidebarContent = document.getElementById('sidebar-scrollable-content');
      if (sidebarContent) {
        sidebarContent.scrollTop = 0;
      }
    }
  }, [isOpen, tutorialStartStep]);

  // Handle navigation when reaching specific tutorial steps
  useEffect(() => {
    if (!isOpen) return;

    const currentStepId = TUTORIAL_STEPS[currentStep]?.stepId;

    // Scroll sidebar to top for steps that highlight sidebar elements
    const sidebarSteps = ['sidebar', 'areas', 'createArea', 'services', 'processes'];
    if (sidebarSteps.includes(currentStepId)) {
      const sidebarContent = document.getElementById('sidebar-scrollable-content');
      if (sidebarContent) {
        sidebarContent.scrollTop = 0;
      }
    }

    // Navigate to /areas when on createArea step
    if (currentStepId === 'createArea' && location.pathname !== '/areas') {
      history.push('/areas');
    }

    // Navigate to /processes when on processesList step
    if (currentStepId === 'processesList' && !location.pathname.startsWith('/processes')) {
      history.push('/processes?tutorial=true');
    }

    // Scroll to top of the page when on processesList step
    if (currentStepId === 'processesList') {
      const timer = setTimeout(() => {
        if (!isOpen) return; // Don't scroll if modal was closed
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 300);
      return () => clearTimeout(timer);
    }

    // Navigate to first process when on processDescriptionSidebar step
    if (currentStepId === 'processDescriptionSidebar' && !location.pathname.match(/^\/processes\/.+/)) {
      // The process list page will handle navigation to the first process
      history.push('/processes?tutorial=openFirst');
    }

    // Scroll process description sidebar to the left when on processDescriptionSidebar step
    if (currentStepId === 'processDescriptionSidebar') {
      const timer = setTimeout(() => {
        if (!isOpen) return; // Don't scroll if modal was closed
        const processSidebar = document.querySelector('[data-tutorial-id="process-sidebar"]');
        if (processSidebar) {
          const scrollableParent = processSidebar.closest('[style*="overflow"]') || processSidebar.parentElement;
          if (scrollableParent) {
            scrollableParent.scrollLeft = 0;
          }
        }
      }, 500);
      return () => clearTimeout(timer);
    }

    // Stay on process description for the content step
    if (currentStepId === 'processDescriptionContent' && !location.pathname.match(/^\/processes\/.+/)) {
      // Already on a process page, no navigation needed
    }

    // Stay on process description for the triggers step
    if (currentStepId === 'triggers' && !location.pathname.match(/^\/processes\/.+/)) {
      history.push('/processes?tutorial=openFirst');
    }

    // Navigate to /processMap for the overview and filters steps
    if ((currentStepId === 'processMapOverview' || currentStepId === 'processMapFilters') && location.pathname !== '/processMap') {
      history.push('/processMap?tutorial=true');
    }
  }, [currentStep, isOpen, history, location.pathname]);

  // Auto-advance to next step after 30 seconds of inactivity
  useEffect(() => {
    if (!isOpen || isLastStep) return;

    const timer = setTimeout(() => {
      setCurrentStep(currentStep + 1);
    }, 30000); // 30 seconds

    return () => {
      clearTimeout(timer);
    };
  }, [currentStep, isOpen, isLastStep]);

  const updateTutorialPreferences = async (updates: Partial<UserPreferences>) => {
    setIsUpdating(true);
    try {
      // Initialize preferences if null
      const basePreferences = preferences || {};
      // Remove legacy flat keys when migrating to nested structure
      const { tutorialCompleted, tutorialDismissed, tutorialVersion, tutorialLastStep, ...cleanedPreferences } = basePreferences;
      const updatedPreferences: UserPreferences = {
        ...cleanedPreferences,
        ...updates,
      };

      const updateResult: updateUserPreferencesRequest = await graphQlClient.request(
        UserGraphQL.mutations.updateUserPreferences,
        {
          input: {
            preferences: updatedPreferences,
          },
        },
      );

      if (updateResult.updateUserPreferences) {
        dispatch(setUserPreferences(updatedPreferences));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to update tutorial preferences:', error);
      dispatch(
        appendActionMessage({
          message: localeActionMessages['errorUpdatingPreferences'],
          type: SnackType.ERROR,
        }),
      );
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleComplete = async () => {
    const success = await updateTutorialPreferences({
      tutorial: {
        ...preferences?.tutorial,
        completed: true,
        dismissed: true,
        version: TUTORIAL_VERSION,
      },
    });

    if (success) {
      dispatch(
        appendActionMessage({
          message: localeTutorial['tutorialCompleted'] || 'Tutorial completed!',
          type: SnackType.SUCCESS,
        }),
      );
      onClose();
    }
  };

  const handleSkip = async () => {
    // Always close the modal, even if preference update fails
    onClose();

    // Update preferences in background (don't block on success)
    updateTutorialPreferences({
      tutorial: {
        ...preferences?.tutorial,
        completed: false,
        dismissed: true,
        lastStep: currentStep,
      },
    });
  };

  const handleElementNotFound = () => {
    // Show error toast
    dispatch(
      appendActionMessage({
        message: localeTutorial['unableToCompleteTutorial'] || 'Unable to complete tutorial',
        type: SnackType.ERROR,
      }),
    );

    // Close the tutorial
    onClose();
  };

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Don't render anything if modal is closed or no step data
  if (!isOpen || !currentStepData) {
    return null;
  }

  // For modal type steps
  if (currentStepData.spotlightType === 'modal') {
    const stepContent = localeTutorial[currentStepData.contentKey];
    return (
      <CustomModal
        isOpen={isOpen}
        onClose={handleSkip}
        title={stepContent?.['title'] || ''}
        maxWidth="600px"
        showButtons={false}
        rounded={true}>
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {stepContent?.['content'] || ''}
          </Typography>

          {/* Step indicators */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
            {TUTORIAL_STEPS.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: index === currentStep ? 'primary.main' : 'grey.300',
                  transition: 'background-color 0.3s',
                }}
              />
            ))}
          </Box>

          {/* Step counter */}
          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mb: 3 }}>
            {localeTutorial['stepCounter']
              ?.replace('{current}', (currentStep + 1).toString())
              .replace('{total}', TUTORIAL_STEPS.length.toString())}
          </Typography>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <WolfButton
              variant="contained"
              color='primary'
              onClick={handleSkip}
              disabled={isUpdating}>
              {localeTutorial['skipTutorial']}
            </WolfButton>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {!isFirstStep && (
                <WolfButton
                  variant="contained"
              color='primary'
                  onClick={handlePrevious}
                  disabled={isUpdating}>
                  {localeTutorial['previousStep']}
                </WolfButton>
              )}
              <WolfButton variant="contained" color="primary" onClick={handleNext} disabled={isUpdating}>
                {isLastStep ? localeTutorial['getStarted'] : localeTutorial['nextStep']}
              </WolfButton>
            </Box>
          </Box>
        </Box>
      </CustomModal>
    );
  }

  // For overlay type steps
  if (currentStepData.spotlightType === 'overlay' && currentStepData.targetSelector) {
    const stepContent = localeTutorial[currentStepData.contentKey];
    return (
      <TutorialSpotlight
        targetSelector={currentStepData.targetSelector}
        title={stepContent?.['title'] || ''}
        content={stepContent?.['content'] || ''}
        position={currentStepData.position}
        onElementNotFound={handleElementNotFound}>
        {/* Step indicators */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, mt: 1 }}>
          {TUTORIAL_STEPS.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: index === currentStep ? 'primary.main' : 'grey.300',
                transition: 'background-color 0.3s',
              }}
            />
          ))}
        </Box>

        {/* Step counter */}
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mb: 2 }}>
          {localeTutorial['stepCounter']
            ?.replace('{current}', (currentStep + 1).toString())
            .replace('{total}', TUTORIAL_STEPS.length.toString())}
        </Typography>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <WolfButton variant="contained" color="primary" size="small" onClick={handleSkip} disabled={isUpdating}>
            {localeTutorial['skipTutorial']}
          </WolfButton>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isFirstStep && (
              <WolfButton
                variant="contained"
                color="primary"
                size="small"
                onClick={handlePrevious}
                disabled={isUpdating}>
                {localeTutorial['previousStep']}
              </WolfButton>
            )}
            <WolfButton variant="contained" color="primary" size="small" onClick={handleNext} disabled={isUpdating}>
              {localeTutorial['nextStep']}
            </WolfButton>
          </Box>
        </Box>
      </TutorialSpotlight>
    );
  }

  return null;
};

export default TutorialModal;
